// src/components/dstack/DstackPage2.tsx
import React, { useState, useRef } from 'react';
import './Dstack2.css';
import dstackService from '../../services/dstackService';

import DstackQueryEditor from './DstackQueryEditor';
import DstackResultViewer from './DstackResultViewer';

// ---- ---- ---- ---- ---- ---- ---- ---- ---- ---- ---- ---- 
//  DStackPage2 - page container for Dstack v2 page

const DstackPage2: React.FC = () => {
  const [callStack, setCallStack] = useState<string>('');
  const [result, setResult] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false); // Add loading state
  const [error, setError] = useState<string | null>(null); // Add error state
  const [processingSteps, setProcessingSteps] = useState<string[]>([]);
  const resultViewerRef = useRef<HTMLDivElement | null>(null);

  const handleRunQuery = async (queryText: string, codeSnippet?: string) => {
    setCallStack(queryText); // Keep track of the submitted query
    setIsLoading(true);
    setError(null);
    setResult(''); // Clear previous result
    setProcessingSteps([ // Optional: Update steps immediately
      '1. Analyzing your query...',
      '2. Calling backend service...',
    ]);

    // Focus logic can stay here or move after API call
    if (resultViewerRef.current) {
       resultViewerRef.current.scrollIntoView({ behavior: 'smooth' });
    }

    try {
      // Assuming dstackService.getInsight takes the query and returns { dstack: 'markdown_result' }
      const response = await dstackService.getDstack(queryText, codeSnippet); // Use your actual service method
      setResult(response.answer || 'No result received.'); // Update result with actual data
      setProcessingSteps([ // Update steps on success
         'Processing complete.',
         'Here is your response:'
      ]);
    } catch (err: any) {
      console.error("Error fetching Dstack insight:", err);
      const errorMessage = err.response?.data?.message || err.message || 'An unknown error occurred.';
      setError(`Failed to get insight: ${errorMessage}`);
      setResult(`Error: ${errorMessage}`); // Show error in result area too
      setProcessingSteps(['Error occurred during processing.']); // Update steps on error
    } finally {
      setIsLoading(false); // Ensure loading is set to false
    }
  };

  // Display loading/error states
  const displayResult = isLoading ? "Processing..." : (error || result);

  return (
    <div className="dstack2-container">

      {/* Call Stack Input */}
      <DstackQueryEditor 
        onRunQuery={handleRunQuery}
        isLoading={isLoading}
      />


      {/* Output Area */}
      <div className="output-section">

        <DstackResultViewer 
          result={displayResult} 
          isLoading={isLoading}
          error={error} 
          ref={resultViewerRef}/>
      </div>
    </div>
  );
};

export default DstackPage2;
